// The whole model's performance, in the same two readings and the same visual
// language the tree page uses per tree: recall on the left (a small share of
// leads should carry a large share of conversions, visible as the two bars not
// lining up) and precision on the right (what a lead in each tier is worth).
//
// This is the ensemble, not one tree, so the scope label says so. The tree
// page's charts cut ONE tree's leaf rates; these numbers come off the labelled
// ensemble, which is what actually ships. The two sit on different pages and the
// seam still has to be readable, which is why both carry their scope in the
// header rather than in a caption.
//
// Replaces recharts on this page. Two stacked recharts canvases spent 800px of
// height on eight numbers, could not be folded, and rounded to two decimals
// where the rest of the product rounds to whole percent. The markup and the CSS
// here are the tree page's, so the two surfaces cannot drift apart visually.
import React from 'react';
import { observer } from 'mobx-react';
import { Card } from 'react-bootstrap';

import ModelPerformances from '../../../models/ModelPerformances';
import {
  SEGMENTS,
  Vocabulary,
  segmentFill,
  segmentInk,
  segmentLabel,
} from './segmentVocabulary';
import {
  pct,
  rate,
  count,
  MIN_INSIDE_LABEL,
  StackedBar,
  BandTier,
} from '../trees/performanceBands';
import { shareLabels } from './scoreLadder';
import '../trees/treePerformanceCharts.css';

interface Tier extends BandTier {
  label: string;
  population?: number;
  conversions?: number;
  leadShare: number;
  conversionShare: number;
  conversionRate: number;
  barPercent: number;
}

/**
 * The repartition rolled up into the four fit tiers, best first.
 *
 * All four are seeded whether or not the model puts anyone in them, so the
 * number of bands does not change as the model is retuned. A tier that
 * disappears reads as a rendering fault; an empty tier reads as an empty tier.
 *
 * Shares are recomputed from the absolute counts where the payload carries
 * them, because the shares on the wire are rounded to whole percent server side
 * and four rounded numbers do not have to sum to 100. Drawing a stacked track
 * from them leaves a sliver of track showing, or clips the last band. Older
 * cached payloads have no absolutes, so the rounded shares stay as the
 * fallback: slightly imprecise geometry beats no chart.
 */
export function performanceTiers(
  performances: ModelPerformances,
  vocabulary: Vocabulary = 'customer_fit'
): {
  tiers: Tier[];
  /** Absent when the payload carries no absolute counts. Never zero-filled. */
  totals?: { population: number; conversions: number };
} {
  const repartition = performances?.repartition ?? {};
  const segments = SEGMENTS[vocabulary];
  const rows = segments.map((segment) => repartition[segment]);

  // Every tier the model reports has to carry a count, or the shares cut from
  // them would each be against a different denominator. A tier the model puts
  // nobody in is simply absent from the repartition and does not count against
  // this.
  const reported = rows.filter(Boolean);
  const hasAbsolutes =
    reported.length > 0 &&
    reported.every((row) => Number.isFinite(Number(row.leadsAbsolute)));

  const population = rows.reduce(
    (sum, row) => sum + (Number(row?.leadsAbsolute) || 0),
    0
  );
  const conversions = rows.reduce(
    (sum, row) => sum + (Number(row?.conversionsAbsolute) || 0),
    0
  );
  const exact = hasAbsolutes && population > 0;

  const rates = rows.map((row) => Number(row?.rate) || 0);
  const bestRate = Math.max(...rates);

  const tiers = segments.map((segment, index) => {
    const row = rows[index];
    const leads = Number(row?.leadsAbsolute) || 0;
    const wins = Number(row?.conversionsAbsolute) || 0;

    return {
      segment,
      label: segmentLabel(vocabulary, segment),
      color: segmentFill(vocabulary, segment),
      textColor: segmentInk(vocabulary, segment),
      population: exact ? leads : undefined,
      conversions: exact ? wins : undefined,
      leadShare: exact
        ? (leads / population) * 100
        : Number(row?.leadsShare) || 0,
      conversionShare:
        exact && conversions > 0
          ? (wins / conversions) * 100
          : Number(row?.conversionsShare) || 0,
      conversionRate: rates[index],
      // Scaled so the best converting tier fills its track. A flat 0 to 100
      // axis draws every bar as a sliver at the base rates fit models run at,
      // which is the case the chart exists to make readable.
      barPercent: bestRate > 0 ? (rates[index] / bestRate) * 100 : 0,
    };
  });

  return { tiers, totals: exact ? { population, conversions } : undefined };
}

function ModelPerformanceBands({
  performances,
  open,
  onToggleOpen,
  title = 'Model Performance',
  scope = 'All trees, current weights',
  vocabulary = 'customer_fit',
  unit = 'leads',
}: {
  performances: ModelPerformances;
  /** Which four segments the repartition is keyed by. Fit unless said. */
  vocabulary?: Vocabulary;
  /** What a row of the repartition counts: an account model scores accounts. */
  unit?: 'leads' | 'accounts';
  /**
   * What this pair of charts is OF. Defaulted to what every existing caller
   * shows, so naming it is opt in: the same charts now also draw the
   * with-overrides run, and two identically headed cards on one page would be
   * the reader's problem to solve.
   */
  title?: string;
  scope?: string;
  /**
   * Whether the charts are shown. Folded, the header keeps the headline reading,
   * so the section still says something on a page that also holds the controls
   * that change it. Omitted means always open.
   */
  open?: boolean;
  onToggleOpen?: () => void;
}) {
  const { tiers, totals } = performanceTiers(performances, vocabulary);
  const Unit = unit === 'accounts' ? 'Accounts' : 'Leads';

  // The tiers arrive best first, so the top two are Very Good and Good. Read off
  // the array rather than matched by name: a tenant can rename a segment and the
  // ORDER is what carries the meaning.
  // Apportioned by largest remainder, so the four printed labels total 100.
  // Rounding each share on its own gave a leads bar reading 11 + 25 + 42 + 23,
  // which is 101.
  const leadLabels = shareLabels(tiers.map((tier) => tier.leadShare));
  const conversionLabels = shareLabels(
    tiers.map((tier) => tier.conversionShare)
  );

  // The headline is the SUM OF THE PRINTED LABELS, not a second rounding of the
  // same quantity. Summing the exact shares and rounding once put "81% of
  // conversions" above a bar reading 48 and 32, and "35% of leads" above one
  // reading 11 and 25. Both were defensible on their own and disagreed on
  // screen, which is the only thing a reader can check.
  const top = tiers.slice(0, 2);
  const headline = {
    topRecall: conversionLabels[0] + conversionLabels[1],
    topLeads: leadLabels[0] + leadLabels[1],
    topLabels: top.map((tier) => tier.label).join(' and '),
  };

  return (
    <Card className={`tpf-card ${open === false ? 'tpf-card--collapsed' : ''}`}>
      <Card.Body>
        <div className="tpf-head">
          <button
            type="button"
            className="tpf-toggle"
            onClick={onToggleOpen}
            aria-expanded={open !== false}
            disabled={!onToggleOpen}
          >
            <span className="tpf-chevron" aria-hidden="true">
              {open === false ? '►' : '▼'}
            </span>
            <span className="tpf-tree">{title}</span>
            {/* Cut from the labelled ensemble, so this covers every tree at its
                configured weight. Said out loud because the same two charts on
                the tree page cover one tree. */}
            {/* Skipped when empty, so a caller whose title says it all does not
                carry a second phrase saying it again. */}
            {scope && <span className="tpf-scope">{scope}</span>}
          </button>
          {/* Absent on payloads cached before the counts were carried through.
              Nothing is invented in that case; the line is simply not there. */}
          {totals && (
            <span className="tpf-totals">
              {count(totals.population)} {unit} &middot;{' '}
              {count(totals.conversions)} conversions
            </span>
          )}
        </div>

        {/* The same sentence the Tiering card leads with, in the same words and
            the same shape, because it answers the same question about a
            different scoring of the same leads. It used to appear only when the
            section was folded, which meant the two cards read alike closed and
            nothing alike open.

            Recall is a ratio of two shares and needs both halves stated: "81% of
            conversions" says nothing until you know whether that came out of 35%
            of the leads or 90% of them.

            It used to end with "best tier converts X%", which was wrong twice.
            "Best tier" named the TOP tier while printing the highest conversion
            RATE, and those are not the same tier once overrides move leads: on a
            model whose overrides push volume into Medium, the sentence read "0%
            of leads make 0% of conversions. Best tier converts 7.6%" with the
            7.6% belonging to Medium. It also put a rate and two shares in one
            sentence, all wearing a percent sign. The rates are in the precision
            chart below, against the tier they belong to. */}
        <p className="tpf-headline">
          <b>{pct(headline.topLeads)}</b> of {unit} (
          {headline.topLabels.toLowerCase()}) make{' '}
          <b>{pct(headline.topRecall)}</b> of conversions.
        </p>

        {/* One note for both charts, ABOVE the grid rather than under either
            heading. The two columns are equal grid tracks, so a note in one and
            not the other pushed that column's bars down; and a note in each
            still breaks alignment the moment one wraps to a second line and the
            other does not, which at 992px it does. Above the grid neither can
            happen, whatever it wraps to. */}
        <p className="tpf-note" hidden={open === false}>
          Precision percentages are conversion rates. The rest are shares of the
          total.
        </p>

        <div
          className="tpf-charts"
          hidden={open === false}
          /* `hidden` rather than unmounting: the bars are pure derived state, so
             re-creating them on every open costs a layout pass for nothing. */
        >
          <section className="tpf-chart">
            {/* Named for what the two bars ARE, matching the Tiering card's
                LEADS and CONVERSIONS above the same pair of readings. The old
                title described what the reader should conclude from them, which
                is a different job and left the two cards calling the same thing
                by two names. `TreePerformanceCharts` keeps its own wording; it
                has no Tiering card beside it to agree with. */}
            <h6 className="tpf-title">{Unit} and conversions</h6>
            <StackedBar
              label={Unit}
              tiers={tiers}
              shareOf={(tier) => tier.leadShare}
              labelOf={(_tier, index) => leadLabels[index]}
              // The headline sums these two, so they must be readable.
              alwaysLabel={2}
              describe={(tier) =>
                tier.population === undefined
                  ? `${tier.label}: ${pct(tier.leadShare)} of ${unit}`
                  : `${tier.label}: ${count(tier.population)} ${unit}, ${pct(
                      tier.leadShare
                    )} of the model`
              }
            />
            <StackedBar
              label="Conversions"
              tiers={tiers}
              shareOf={(tier) => tier.conversionShare}
              labelOf={(_tier, index) => conversionLabels[index]}
              alwaysLabel={2}
              describe={(tier) =>
                tier.conversions === undefined
                  ? `${tier.label}: ${pct(tier.conversionShare)} of conversions`
                  : `${tier.label}: ${count(
                      tier.conversions
                    )} conversions, ${pct(tier.conversionShare)} of the model`
              }
            />
            {/* One legend for both bars: same four tiers in the same order, and
                a key per bar would say so twice. */}
            <div className="tpf-legend">
              {tiers.map((tier) => (
                <span className="tpf-legend-item" key={tier.segment}>
                  <span
                    className="tpf-legend-dot"
                    style={{ background: tier.color }}
                  />
                  {tier.label}
                </span>
              ))}
            </div>
          </section>

          <section className="tpf-chart">
            <h6 className="tpf-title">Precision</h6>
            {/* Fixed tier order rather than sorted by length. The tiers are cut
                from the conversion rate itself, so best to worst already is
                longest to shortest, and rows that move as the weights change
                read as a bug. */}
            {tiers.map((tier) => (
              <div className="tpf-rate-row" key={tier.segment}>
                <span className="tpf-rate-label">{tier.label}</span>
                <div className="tpf-rate-track">
                  <span
                    className="tpf-rate-fill"
                    style={{
                      width: `${tier.barPercent}%`,
                      background: tier.color,
                    }}
                  >
                    {tier.barPercent >= MIN_INSIDE_LABEL && (
                      <span
                        className="tpf-rate-value"
                        style={{ color: tier.textColor }}
                      >
                        {rate(tier.conversionRate)}
                      </span>
                    )}
                  </span>
                  {tier.barPercent < MIN_INSIDE_LABEL && (
                    <span className="tpf-rate-value tpf-rate-value--outside">
                      {rate(tier.conversionRate)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </section>
        </div>
      </Card.Body>
    </Card>
  );
}

// Observed so the charts redraw when Save & Compute lands a new repartition on
// the store, the same reason the tree charts are observed.
export default observer(ModelPerformanceBands);
